import type { WexlerStandardField } from './relativity-field-mapping.entity.js';
import {
  WEXLER_TEXT_FIELDS,
  WEXLER_TEXT_ARRAY_FIELDS,
  WEXLER_NUMBER_FIELDS,
  WEXLER_DATE_FIELDS,
} from './relativity-constants.js';

/**
 * Relativity field types as returned by the Relativity API
 * Based on Relativity Field Manager API documentation
 */
export enum RelativityFieldType {
  FixedLengthText = 'Fixed-Length Text',
  LongText = 'Long Text',
  WholeNumber = 'Whole Number',
  Decimal = 'Decimal',
  Currency = 'Currency',
  Date = 'Date',
  YesNo = 'Yes/No',
  SingleObject = 'Single Object',
  MultipleObject = 'Multiple Object',
  SingleChoice = 'Single Choice',
  MultipleChoice = 'Multiple Choice',
  User = 'User',
  File = 'File',
}

/**
 * Wexler field data types based on RelativityMetadata entity column types
 */
export enum WexlerFieldDataType {
  Text = 'text',
  TextArray = 'text_array',
  Number = 'number',
  Date = 'date',
  Custom = 'custom', // Can be string | number | boolean | null in customFields JSONB
}

/**
 * Type compatibility result
 */
export enum TypeCompatibility {
  Valid = 'valid', // Fully compatible, no issues
  Info = 'info', // Compatible with informational message
  Warning = 'warning', // Compatible but may lose precision or require conversion
  Error = 'error', // Incompatible, cannot be mapped
}

export interface TypeValidationResult {
  compatible: TypeCompatibility;
  message: string | null;
}

/**
 * Get the Wexler data type for a standard field
 */
export function getWexlerFieldDataType(wexlerField: WexlerStandardField): WexlerFieldDataType {
  // Custom fields can accept any type
  if (wexlerField === 'custom') {
    return WexlerFieldDataType.Custom;
  }

  if (WEXLER_TEXT_ARRAY_FIELDS.includes(wexlerField)) {
    return WexlerFieldDataType.TextArray;
  }

  if (WEXLER_TEXT_FIELDS.includes(wexlerField)) {
    return WexlerFieldDataType.Text;
  }

  if (WEXLER_NUMBER_FIELDS.includes(wexlerField)) {
    return WexlerFieldDataType.Number;
  }

  if (WEXLER_DATE_FIELDS.includes(wexlerField)) {
    return WexlerFieldDataType.Date;
  }

  // Default to text for unknown fields
  return WexlerFieldDataType.Text;
}

/**
 * Validate type compatibility between Relativity field type and Wexler field
 */
// eslint-disable-next-line max-statements
export function validateTypeCompatibility(
  relativityFieldType: string,
  wexlerField: WexlerStandardField | null,
): TypeValidationResult {
  // If no Wexler field selected, no validation needed
  if (wexlerField === null) {
    return {
      compatible: TypeCompatibility.Valid,
      message: null,
    };
  }

  const wexlerDataType = getWexlerFieldDataType(wexlerField);

  // Block Single Object and Multiple Object entirely
  if (
    relativityFieldType === RelativityFieldType.SingleObject ||
    relativityFieldType === RelativityFieldType.MultipleObject
  ) {
    return {
      compatible: TypeCompatibility.Error,
      message:
        'Single Object and Multiple Object fields cannot be mapped. These fields reference other Relativity objects.',
    };
  }

  // Custom fields accept any type except Single/Multiple Object
  if (wexlerDataType === WexlerFieldDataType.Custom) {
    return {
      compatible: TypeCompatibility.Valid,
      message: null,
    };
  }

  // Text array target field
  if (wexlerDataType === WexlerFieldDataType.TextArray) {
    // Text sources are fully compatible (will be split by separator)
    if (
      relativityFieldType === RelativityFieldType.FixedLengthText ||
      relativityFieldType === RelativityFieldType.LongText
    ) {
      return {
        compatible: TypeCompatibility.Valid,
        message: null,
      };
    }

    // Multiple Choice can map to text array
    if (relativityFieldType === RelativityFieldType.MultipleChoice) {
      return {
        compatible: TypeCompatibility.Info,
        message: 'Multiple choice values will be stored as text array.',
      };
    }

    // Single Choice to array: allowed with info
    if (relativityFieldType === RelativityFieldType.SingleChoice) {
      return {
        compatible: TypeCompatibility.Info,
        message: 'Single choice value will be stored as single-item array.',
      };
    }

    // Other types not ideal for arrays
    return {
      compatible: TypeCompatibility.Warning,
      message:
        'This field type is better suited for text fields. Consider mapping to a text or custom field instead.',
    };
  }

  // Text target field
  if (wexlerDataType === WexlerFieldDataType.Text) {
    // Text sources are fully compatible
    if (
      relativityFieldType === RelativityFieldType.FixedLengthText ||
      relativityFieldType === RelativityFieldType.LongText
    ) {
      return { compatible: TypeCompatibility.Valid, message: null };
    }

    // Date to text: allowed with warning
    if (relativityFieldType === RelativityFieldType.Date) {
      return {
        compatible: TypeCompatibility.Warning,
        message:
          'Date will be stored as ISO string in text field. Consider mapping to a date or custom field.',
      };
    }

    // Number to text: allowed with warning
    if (
      relativityFieldType === RelativityFieldType.WholeNumber ||
      relativityFieldType === RelativityFieldType.Decimal ||
      relativityFieldType === RelativityFieldType.Currency
    ) {
      return {
        compatible: TypeCompatibility.Info,
        message: 'Number source will be converted to text.',
      };
    }

    // Boolean to text: allowed with warning
    if (relativityFieldType === RelativityFieldType.YesNo) {
      return {
        compatible: TypeCompatibility.Info,
        message: 'Yes/No will be stored as "true"/"false" text.',
      };
    }

    // Choice types to text: allowed with warning
    if (
      relativityFieldType === RelativityFieldType.SingleChoice ||
      relativityFieldType === RelativityFieldType.MultipleChoice
    ) {
      return {
        compatible: TypeCompatibility.Info,
        message: 'Choice field values will be stored as text.',
      };
    }

    // User and File types can be converted to text
    if (
      relativityFieldType === RelativityFieldType.User ||
      relativityFieldType === RelativityFieldType.File
    ) {
      return {
        compatible: TypeCompatibility.Warning,
        message: 'Field value will be converted to text representation.',
      };
    }
  }

  // Number target field
  if (wexlerDataType === WexlerFieldDataType.Number) {
    // Number sources are fully compatible
    if (
      relativityFieldType === RelativityFieldType.WholeNumber ||
      relativityFieldType === RelativityFieldType.Decimal ||
      relativityFieldType === RelativityFieldType.Currency
    ) {
      return { compatible: TypeCompatibility.Valid, message: null };
    }

    // Text to number: allowed with warning about non-numeric values
    if (
      relativityFieldType === RelativityFieldType.FixedLengthText ||
      relativityFieldType === RelativityFieldType.LongText
    ) {
      return {
        compatible: TypeCompatibility.Warning,
        message:
          'Text will be converted to number. If non-numeric text is present in the field, it will not be stored.',
      };
    }

    // Cannot convert other types to numbers
    return {
      compatible: TypeCompatibility.Error,
      message:
        'Cannot convert this field type to a number. Please map to a text field or custom field instead.',
    };
  }

  // Date target field
  if (wexlerDataType === WexlerFieldDataType.Date) {
    // Only Date type can map to date fields
    if (relativityFieldType === RelativityFieldType.Date) {
      return { compatible: TypeCompatibility.Valid, message: null };
    }

    // Cannot convert non-date types to dates
    return {
      compatible: TypeCompatibility.Error,
      message:
        'Only Relativity Date fields can be mapped to Wexler date fields. Please map to a text field or custom field instead.',
    };
  }

  // Default: unknown combination, show error
  return {
    compatible: TypeCompatibility.Error,
    message: 'Unsupported field type combination.',
  };
}

/**
 * Validate all field mappings in a configuration
 * Returns a map of field artifact IDs to validation results
 */
export function validateFieldMappings(mappings: {
  [relativityArtifactId: string]: {
    relativityFieldType: string;
    wexlerField: WexlerStandardField | null;
  };
}): Record<string, TypeValidationResult> {
  const results: Record<string, TypeValidationResult> = {};

  for (const [artifactId, config] of Object.entries(mappings)) {
    results[artifactId] = validateTypeCompatibility(config.relativityFieldType, config.wexlerField);
  }

  return results;
}

/**
 * Check if field mappings contain any errors (incompatible types)
 */
export function hasValidationErrors(
  validationResults: Record<string, TypeValidationResult>,
): boolean {
  return Object.values(validationResults).some(
    (result) => result.compatible === TypeCompatibility.Error,
  );
}

/**
 * Get all validation error messages
 */
export function getValidationErrorMessages(
  validationResults: Record<string, TypeValidationResult>,
): string[] {
  return Object.values(validationResults)
    .filter((result) => result.compatible === TypeCompatibility.Error && result.message !== null)
    .map((result) => result.message as string);
}
